import React, { useCallback, useEffect, useMemo } from 'react';
import t from 'react-translate';
import { useSelector } from 'react-redux';

import { fetchQuestionSet } from 'redux/actions/analytics';
import { useAppDispatch } from 'redux/store';
import NvDropdown, {
  NvDropdownOption, NvDropdownButtonStyle, NvDropdownAlign,
  NvDropdownAccordionHeader,
} from 'shared/components/inputs/nv-dropdown';
import { find, keys, values } from 'underscore';
import NvIcon from 'shared/components/nv-icon';

const keyMapping = {
  quizzes: 'QUIZ',
  timedExams: 'TIMED_EXAM',
  surveys: 'SURVEY',
};

const QuizAndSurvey = (props: { onSelect: (id) => void, selectedId: number }) => {
  const institutionId = useSelector(state => state.app.currentInstitutionId);
  const catalogId = useSelector((state) => state.app.currentCatalogId);
  const { questionSets, questionSetsLoaded } = useSelector(state => state.app.analytics);

  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(fetchQuestionSet({ institutionId, catalogId }));
  }, [catalogId, dispatch, institutionId]);

  const onSelectQuestionSet = useCallback((questionSetId) => {
    props.onSelect(questionSetId);
  }, [props]);

  const totalNumberofQuestionSets = useMemo(() => values(questionSets)?.flat()?.length ?? 0, [questionSets]);

  const getCategories = useMemo(() => {
    const categories: NvDropdownOption[] = keys(questionSets)?.map(key => ({
      type: 'accordion-header',
      title: t.COURSE_ANALYTICS[keyMapping[key]](),
      key,
      expanded: totalNumberofQuestionSets < 10 ? true : questionSets[key]?.length < 5,
      items: questionSets[key].map(item => ({
        type: 'text',
        text: item.title,
        id: item.id,
        key,
        isChecked: item.id === props.selectedId,
        callback: () => onSelectQuestionSet(item.id),
      })),
    }));
    return categories;
  }, [questionSets, totalNumberofQuestionSets, props.selectedId, onSelectQuestionSet]);

  const findInitialIndex = useMemo(() => {
    let initialIndex = -1;

    getCategories.find((value: NvDropdownAccordionHeader) => {
      initialIndex = value.items.findIndex(item => item.id === props.selectedId);
      return initialIndex >= 0; // Return true if the index is valid
    });
    return initialIndex;
  }, [getCategories, props.selectedId]);

  return (
    <NvDropdown
      buttonStyle={NvDropdownButtonStyle.CUSTOM}
      items={getCategories}
      align={NvDropdownAlign.LEFT}
      noMenuPadding
      initialIndex={findInitialIndex}
      showSelectedIndicator={!!props.selectedId}
      customTarget={() => (
        questionSetsLoaded && (
          <div className='d-flex align-items-center card-title px-4'>
            {t.COURSE_ANALYTICS.SURVEY_AND_QUIZ()}
            <NvIcon icon='dropdown-arrow' size='xss-smallest' className='ml-3 mt-2' />
          </div>
        )
      )}
    />
  );
};

export default QuizAndSurvey;
